import { useMemo } from 'react';
import { loadState, formatUSD } from '../lib/storage';
import { Printer, Church } from 'lucide-react';

export default function BalanceSheet() {
  const state = useMemo(() => loadState(), []);
  const { categories, accounts, transactions, monthlyBudget } = state;

  const totalAssets = accounts.reduce((a, x) => a + x.balance, 0);
  const totalBudget = categories.reduce((a, c) => a + Number(c.budget || 0), 0);
  const totalSpent = categories.reduce((a, c) => a + Number(c.spent || 0), 0);
  const income = transactions.filter((t) => t.type === 'income').reduce((a, t) => a + t.amount, 0);
  const expenses = transactions.filter((t) => t.type === 'expense').reduce((a, t) => a + Math.abs(t.amount), 0);
  const netMonthly = income - expenses;

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const handlePrint = () => window.print();

  return (
    <div className="space-y-6" data-testid="balance-sheet-page">
      <div className="no-print flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--gcbc-red-deep)] font-bold">Printable report</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Balance Sheet</h1>
          <p className="text-sm text-slate-600 mt-1">Professional summary ready for print or PDF export.</p>
        </div>
        <button
          onClick={handlePrint}
          data-testid="balance-sheet-print-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gcbc-royal)] hover:bg-[var(--gcbc-royal-deep)] text-white font-semibold shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4" /> Print balance sheet
        </button>
      </div>

      <div className="print-area rounded-2xl bg-white border border-slate-200 shadow-sm">
        <div className="print-page p-6 sm:p-10">
          {/* Report header */}
          <div className="flex items-start justify-between border-b-2 border-[var(--gcbc-royal)] pb-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[var(--gcbc-royal)] text-white flex items-center justify-center">
                <Church className="w-7 h-7" />
              </div>
              <div>
                <div className="font-display text-2xl font-black text-slate-900 leading-tight">Grants Creek Baptist Church</div>
                <div className="text-sm text-slate-600">Financial Balance Sheet · February 2026</div>
              </div>
            </div>
            <div className="text-right text-xs text-slate-600">
              <div className="font-semibold text-slate-800">Report generated</div>
              <div>{today}</div>
            </div>
          </div>

          {/* Snapshot */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            {[
              { l: 'Total assets', v: formatUSD(totalAssets) },
              { l: 'Income (Feb)', v: formatUSD(income) },
              { l: 'Expenses (Feb)', v: formatUSD(expenses) },
              { l: 'Net (Feb)', v: formatUSD(netMonthly) },
            ].map((s) => (
              <div key={s.l} className="border border-slate-300 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{s.l}</div>
                <div className="font-display text-lg font-black text-slate-900 mt-1">{s.v}</div>
              </div>
            ))}
          </div>

          {/* Assets / Accounts */}
          <h3 className="font-display text-lg font-bold text-slate-900 mt-8 mb-3">Assets — Accounts</h3>
          <div className="overflow-x-auto">
            <table className="table gcbc-table w-full">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Type</th>
                  <th className="text-end">Balance</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id}>
                    <td className="font-semibold text-slate-900">{a.name}</td>
                    <td className="text-slate-600">{a.type}</td>
                    <td className="text-end font-semibold text-slate-900">{formatUSD(a.balance)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2} className="font-bold text-slate-900">Total Assets</td>
                  <td className="text-end font-black text-[var(--gcbc-royal-deep)]">{formatUSD(totalAssets)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Category summary */}
          <h3 className="font-display text-lg font-bold text-slate-900 mt-8 mb-3">Monthly Categories — Budget vs Actual</h3>
          <div className="overflow-x-auto">
            <table className="table gcbc-table w-full">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="text-end">Budget</th>
                  <th className="text-end">Spent</th>
                  <th className="text-end">Variance</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => {
                  const variance = Number(c.budget) - Number(c.spent);
                  return (
                    <tr key={c.id}>
                      <td className="font-semibold text-slate-900">{c.name}</td>
                      <td className="text-end">{formatUSD(c.budget)}</td>
                      <td className="text-end">{formatUSD(c.spent)}</td>
                      <td className={`text-end font-semibold ${variance < 0 ? 'text-[var(--gcbc-red-deep)]' : 'text-emerald-700'}`}>
                        {variance < 0 ? `− ${formatUSD(Math.abs(variance))}` : formatUSD(variance)}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td className="font-bold text-slate-900">Totals</td>
                  <td className="text-end font-bold">{formatUSD(totalBudget)}</td>
                  <td className="text-end font-bold">{formatUSD(totalSpent)}</td>
                  <td className={`text-end font-black ${totalBudget - totalSpent < 0 ? 'text-[var(--gcbc-red-deep)]' : 'text-emerald-700'}`}>
                    {formatUSD(totalBudget - totalSpent)}
                  </td>
                </tr>
                <tr>
                  <td className="font-bold text-slate-900">Set monthly budget</td>
                  <td className="text-end font-bold" colSpan={2}>{formatUSD(monthlyBudget)}</td>
                  <td className={`text-end font-bold ${monthlyBudget - totalSpent < 0 ? 'text-[var(--gcbc-red-deep)]' : 'text-emerald-700'}`}>
                    {formatUSD(monthlyBudget - totalSpent)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Signatures */}
          <div className="grid sm:grid-cols-2 gap-8 mt-10">
            <div>
              <div className="border-b border-slate-400 h-10"></div>
              <div className="text-xs text-slate-600 mt-1">Treasurer signature</div>
            </div>
            <div>
              <div className="border-b border-slate-400 h-10"></div>
              <div className="text-xs text-slate-600 mt-1">Pastor / Deacon signature</div>
            </div>
          </div>

          <div className="mt-10 pt-4 border-t border-slate-300 text-center text-xs text-slate-500">
            GCBC finances app 2026 · Grants Creek Baptist Church · Stewarding what the Lord provides.
          </div>
        </div>
      </div>
    </div>
  );
}
